import { ReactElement, ReactNode, useId } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Check } from 'lucide-react';

export interface RadioOption {
  id: string;
  label: string;
  value: string;
  /** Incompatible with other answers — shown disabled with a reason. */
  disabled?: boolean;
  disabledReason?: string;
  withText?: boolean;
}

interface RadioQuestionProps {
  question: string;
  hint?: string;
  options: RadioOption[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  rightAdornment?: ReactNode;
  text?: string;
  onTextChange?: (text: string | undefined) => void;
  textMaxLength?: number;
}

const DEFAULT_TEXT_MAX_LENGTH = 200;

/** Custom radio glyph matching the card design: an 18px ring that fills with
 *  primary and shows a check when selected (mirrors the Checkbox component). */
const RadioGlyph = ({ checked = false }: { checked?: boolean }) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '50%',
      border: 2,
      borderColor: checked ? 'primary.main' : 'divider',
      bgcolor: checked ? 'primary.main' : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {checked && <Check size={12} color="white" strokeWidth={3} />}
  </Box>
);

/** Cards-radio question: each option is a clickable card; supports disabled
 *  options (line-through + reason). Ported from wio.funds. */
export const RadioQuestion = ({
  question,
  hint,
  options,
  value,
  onChange,
  required,
  error,
  rightAdornment,
  text,
  onTextChange,
  textMaxLength = DEFAULT_TEXT_MAX_LENGTH,
}: RadioQuestionProps) => {
  const labelId = useId();
  const hintId = useId();

  const handleChange = (next: string) => {
    const previous = options.find((opt) => opt.value === value);

    if (previous?.withText && previous.value !== next)
      onTextChange?.(undefined);

    onChange(next);
  };

  const withTextInput = (opt: RadioOption, card: ReactElement) => (
    <Box
      key={opt.id}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {card}
      <OutlinedInput
        value={text ?? ''}
        onChange={(event) =>
          onTextChange?.(
            event.target.value === '' ? undefined : event.target.value,
          )
        }
        inputProps={{ 'aria-label': opt.label, maxLength: textMaxLength }}
        sx={{ borderRadius: 1.5 }}
      />
    </Box>
  );

  return (
    <FormControl
      component="fieldset"
      variant="standard"
      error={!!error}
      fullWidth
      sx={{ mb: 3 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: hint ? 0.5 : 1.5,
        }}
      >
        <FormLabel
          id={labelId}
          component="legend"
          sx={{
            typography: 'textMd',
            fontWeight: 600,
            color: 'text.primary',
            p: 0,
            '&.Mui-focused, &.Mui-error': { color: 'text.primary' },
          }}
        >
          {question}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
              *
            </Box>
          )}
        </FormLabel>
        {rightAdornment && (
          <Box sx={{ flexShrink: 0, mt: 0.25 }}>{rightAdornment}</Box>
        )}
      </Box>
      {hint && (
        <Typography
          id={hintId}
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}
        >
          {hint}
        </Typography>
      )}
      <RadioGroup
        aria-labelledby={labelId}
        aria-describedby={hint ? hintId : undefined}
        value={value ?? ''}
        onChange={(_, next) => handleChange(next)}
      >
        <Stack spacing={3}>
          {options.map((opt) => {
            const isSelected = value === opt.value;
            const isDisabled = !!opt.disabled;
            const card = (
              <FormControlLabel
                key={opt.id}
                value={opt.value}
                disabled={isDisabled}
                control={
                  <Radio
                    disableRipple
                    icon={<RadioGlyph />}
                    checkedIcon={<RadioGlyph checked />}
                    sx={{ p: 0, mt: 0.25 }}
                  />
                }
                label={
                  <>
                    <Typography
                      variant="textSm"
                      sx={{
                        color: isDisabled ? 'text.disabled' : 'text.primary',
                        textDecoration: isDisabled ? 'line-through' : 'none',
                        lineHeight: 1.4,
                      }}
                    >
                      {opt.label}
                    </Typography>
                    {isDisabled && opt.disabledReason && (
                      <Typography
                        variant="textXs"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          mt: 0.25,
                          fontStyle: 'italic',
                        }}
                      >
                        {opt.disabledReason}
                      </Typography>
                    )}
                  </>
                }
                sx={{
                  m: 0,
                  alignItems: 'flex-start',
                  gap: 3,
                  p: 2,
                  border: 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderRadius: 1.5,
                  bgcolor: isSelected ? 'primary.50' : 'background.paper',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': isDisabled
                    ? {}
                    : {
                        borderColor: isSelected
                          ? 'primary.main'
                          : 'text.secondary',
                      },
                  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                  '&.Mui-disabled': { opacity: 0.5 },
                }}
              />
            );
            return isSelected && opt.withText && onTextChange
              ? withTextInput(opt, card)
              : card;
          })}
        </Stack>
      </RadioGroup>
      {error && <FormHelperText sx={{ mt: 1, ml: 0 }}>{error}</FormHelperText>}
    </FormControl>
  );
};
